// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Gif} from '@app/features/expressions/commands/GifCommands';
import {wrapDesktopLocalResourceURL} from '@app/features/messaging/utils/DesktopResourceUrl';

const GIF_IMAGE_FORMAT_KEYS = ['gif', 'tinygif', 'nanogif'] as const;
const EXPRESSION_IMAGE_FORMATS: Record<string, string> = {
	jpeg: 'jpg',
	jpg: 'jpg',
	png: 'png',
	webp: 'webp',
	avif: 'avif',
	gif: 'gif',
};
const GIF_VIDEO_FORMAT_KEYS = ['mp4', 'tinymp4'] as const;
const DOWNLOAD_CANDIDATES_MAX = 8;

interface GifDownloadTarget {
	url: string;
	contentType: string;
	extension: string;
}

function sanitizeGifFileBaseName(gif: Gif): string {
	const base = (gif.id || gif.slug || 'gif').toLowerCase().replace(/[^a-z0-9_-]+/gu, '-');
	return base.length > 0 ? base.slice(0, 64) : 'gif';
}

function collectFormatTargets(
	gif: Gif,
	keys: ReadonlyArray<string>,
	contentType: string,
	extension: string,
): Array<GifDownloadTarget> {
	const targets: Array<GifDownloadTarget> = [];
	for (const key of keys) {
		const format = gif.media?.[key];
		if (!format) continue;
		if (format.proxy_src) {
			targets.push({url: format.proxy_src, contentType, extension});
		}
		if (format.src) {
			targets.push({url: format.src, contentType, extension});
		}
	}
	return targets;
}

function collectImageTargets(gif: Gif): Array<GifDownloadTarget> {
	const targets = collectFormatTargets(gif, GIF_IMAGE_FORMAT_KEYS, 'image/gif', 'gif');
	if (gif.provider === 'ruseo_baidu') {
		for (const [format, extension] of Object.entries(EXPRESSION_IMAGE_FORMATS)) {
			if (format !== 'gif') targets.push(...collectFormatTargets(gif, [format], `image/${format}`, extension));
		}
	}
	for (const url of [gif.proxy_src, gif.src, ...(gif.provider === 'ruseo_baidu' ? [gif.url] : [])]) {
		if (url && (gif.provider === 'ruseo_baidu' || /\.gif(?:$|\?)/iu.test(url))) {
			targets.push({url, contentType: 'image/gif', extension: 'gif'});
		}
	}
	return targets;
}

function collectVideoTargets(gif: Gif): Array<GifDownloadTarget> {
	return collectFormatTargets(gif, GIF_VIDEO_FORMAT_KEYS, 'video/mp4', 'mp4');
}

async function downloadFirstAvailableTarget(
	targets: Array<GifDownloadTarget>,
	baseName: string,
	signal?: AbortSignal,
	maxBytes?: number,
	strictImage = false,
): Promise<File> {
	const seenUrls = new Set<string>();
	let attempts = 0;
	for (const target of targets) {
		signal?.throwIfAborted();
		if (attempts >= DOWNLOAD_CANDIDATES_MAX) break;
		if (seenUrls.has(target.url)) continue;
		seenUrls.add(target.url);
		attempts += 1;
		try {
			const timeout = AbortSignal.timeout(20_000);
			const response = await fetch(wrapDesktopLocalResourceURL(target.url), {
				signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
			});
			if (!response.ok) continue;
			let blob: Blob;
			if (maxBytes !== undefined && response.body) {
				const reader = response.body.getReader();
				try {
					if (Number(response.headers.get('content-length')) > maxBytes) throw new Error('Expression image too large');
					const chunks: Array<Uint8Array<ArrayBuffer>> = [];
					let size = 0;
					for (;;) {
						const {done, value} = await reader.read();
						if (done) break;
						size += value.byteLength;
						if (size > maxBytes) throw new Error('Expression image too large');
						chunks.push(new Uint8Array(value));
					}
					blob = new Blob(chunks, {type: response.headers.get('content-type') ?? ''});
				} finally {
					await reader.cancel().catch(() => undefined);
					reader.releaseLock();
				}
			} else {
				blob = await response.blob();
			}
			if (blob.size === 0 || (maxBytes !== undefined && blob.size > maxBytes)) continue;
			const contentType = blob.type.toLowerCase().split(';')[0];
			const imageExtension = EXPRESSION_IMAGE_FORMATS[contentType.replace('image/', '')];
			if (strictImage && !imageExtension) continue;
			return new File([blob], `${baseName}.${strictImage ? imageExtension : target.extension}`, {
				type: strictImage ? contentType : target.contentType,
			});
		} catch {}
	}
	throw new Error('Failed to download GIF media');
}

export async function downloadGifAsImageFile(
	gif: Gif,
	options: {signal?: AbortSignal; maxBytes?: number} = {},
): Promise<File> {
	return downloadFirstAvailableTarget(
		collectImageTargets(gif),
		sanitizeGifFileBaseName(gif),
		options.signal,
		options.maxBytes ?? (gif.provider === 'ruseo_baidu' ? 25 * 1024 * 1024 : undefined),
		gif.provider === 'ruseo_baidu',
	);
}

export async function downloadGifAsVideoOrImageFile(gif: Gif): Promise<File> {
	const targets = [...collectVideoTargets(gif), ...collectImageTargets(gif)];
	return downloadFirstAvailableTarget(
		targets,
		sanitizeGifFileBaseName(gif),
		undefined,
		gif.provider === 'ruseo_baidu' ? 25 * 1024 * 1024 : undefined,
		gif.provider === 'ruseo_baidu',
	);
}
